import { useCallback, useEffect, useRef, useState } from "react";

// Soft, generative piano — sparse, slow, meditative notes drawn from a warm
// C-major pentatonic, with a bell/felt-piano timbre (additive partials, quick
// attack, long exponential decay) through a gentle reverb. No audio files
// (nothing to 404); scheduling uses setTimeout so it stays reliable.

const MELODY = [
  261.63, 293.66, 329.63, 392.0, 440.0, // C4 D4 E4 G4 A4
  523.25, 587.33, 659.25, 783.99, 880.0, // C5 D5 E5 G5 A5
];
const LOW = [130.81, 146.83, 164.81, 196.0]; // C3 D3 E3 G3
const PARTIALS = [
  { m: 1, g: 1.0 },
  { m: 2, g: 0.5 },
  { m: 3, g: 0.26 },
  { m: 4, g: 0.14 },
  { m: 6, g: 0.07 },
];

function buildImpulse(ctx, seconds = 4, decay = 2.4) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return buf;
}

export default function useAmbientAudio() {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const busRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(false);
  const stepRef = useRef(0);

  const playNote = useCallback((ctx, dest, freq, when, dur, vel) => {
    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0.0001, when);
    noteGain.gain.exponentialRampToValueAtTime(vel, when + 0.008);
    noteGain.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    noteGain.connect(dest);
    PARTIALS.forEach((p) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = freq * p.m;
      const g = ctx.createGain();
      g.gain.value = p.g;
      o.connect(g);
      g.connect(noteGain);
      o.start(when);
      o.stop(when + dur + 0.05);
    });
  }, []);

  const startEngine = useCallback(() => {
    if (startedRef.current) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    ctxRef.current = ctx;

    const master = ctx.createGain();
    master.gain.value = 0.0001;
    masterRef.current = master;

    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 2600;
    filter.Q.value = 0.2;

    const reverb = ctx.createConvolver();
    reverb.buffer = buildImpulse(ctx);
    const wet = ctx.createGain();
    wet.gain.value = 0.6;
    const dry = ctx.createGain();
    dry.gain.value = 0.7;

    const bus = ctx.createGain();
    busRef.current = bus;
    bus.connect(filter);
    filter.connect(dry);
    dry.connect(master);
    filter.connect(reverb);
    reverb.connect(wet);
    wet.connect(master);
    master.connect(ctx.destination);

    const tick = () => {
      const c = ctxRef.current;
      const b = busRef.current;
      if (!c || !b) return;
      const now = c.currentTime + 0.06;
      const step = stepRef.current;

      // main melodic note
      const f = MELODY[Math.floor(Math.random() * MELODY.length)];
      playNote(c, b, f, now, 3.0, 0.5);

      // occasional gentle harmony
      if (Math.random() < 0.34) {
        const f2 = MELODY[Math.floor(Math.random() * 5)];
        playNote(c, b, f2, now + 0.09, 3.2, 0.28);
      }
      // soft bass root every few steps
      if (step % 4 === 0) {
        const lb = LOW[Math.floor(Math.random() * LOW.length)];
        playNote(c, b, lb, now, 4.6, 0.3);
      }

      stepRef.current = step + 1;
      const next = 1900 + Math.random() * 1800; // 1.9s – 3.7s
      timerRef.current = setTimeout(tick, next);
    };
    tick();
    startedRef.current = true;
  }, [playNote]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (next) {
        startEngine();
        const ctx = ctxRef.current;
        const m = masterRef.current;
        if (ctx && ctx.state === "suspended") ctx.resume();
        if (ctx && m) {
          const t = ctx.currentTime;
          m.gain.cancelScheduledValues(t);
          m.gain.setValueAtTime(Math.max(m.gain.value, 0.0001), t);
          m.gain.exponentialRampToValueAtTime(0.17, t + 2.2);
        }
      } else {
        const ctx = ctxRef.current;
        const m = masterRef.current;
        if (ctx && m) {
          const t = ctx.currentTime;
          m.gain.cancelScheduledValues(t);
          m.gain.setValueAtTime(Math.max(m.gain.value, 0.0001), t);
          m.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
        }
      }
      return next;
    });
  }, [startEngine]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (ctxRef.current) {
        try {
          ctxRef.current.close();
        } catch (e) {
          /* noop */
        }
      }
    };
  }, []);

  return { enabled, toggle };
}
